import React, {useContext, useEffect, useState, useRef, useCallback, useMemo} from "react";
import {Box} from "grommet";
import DataTable from "./DataTable";
import useSWR from "swr";
import {silentFetcher} from "../utils/errors";
import {getCSVConfig} from "../utils/csv";
import {DEFAULT_ROWS_PER_PAGE, NUM_ROWS_EXPORTED_IN_CSV} from "../constants/DataTable";
import {DataTablePreferenceContext} from "./DataTablePreferenceProvider";
import {isUndefined} from "../lib/validation";
import {useRouter} from "next/router";

// Helper to parse URL params for filters
const parseUrlFilters = (query) => {
    const filters = {};
    if (query.q) filters.keyword = query.q;
    if (query.filter) {
        // Handle filter as array (repeated params) or comma-separated string
        filters.filterValues = Array.isArray(query.filter)
            ? query.filter
            : query.filter.split(',').filter(Boolean);
    }
    // dateRange is [start, end] array format - use empty string for missing values
    // Backend expects sparse array like [value,] or [,value], not [value, undefined]
    if (query.dateFrom || query.dateTo) {
        filters.dateRange = [query.dateFrom || '', query.dateTo || ''];
    }
    // amountRange is [min, max] array format - use empty string for missing values
    if (query.amountMin || query.amountMax) {
        filters.amountRange = [
            query.amountMin ? parseFloat(query.amountMin) : '',
            query.amountMax ? parseFloat(query.amountMax) : ''
        ];
    }
    // offset (page position) - only meaningful when > 0; page 1 omits it from the URL
    if (query.offset !== undefined) {
        const parsedOffset = parseInt(query.offset, 10);
        if (!Number.isNaN(parsedOffset) && parsedOffset > 0) filters.offset = parsedOffset;
    }
    return filters;
};

const DataTableWithBackendSearch = ({
    filterParams, // obj - keys: options, default
    filterControls,
    getUrl, // fn - returns query string
    onRowClicked,
    columns,
    reshape,
    hideSearch=false,
    hideDateRangeFilter=false,
    hideAmountRangeFilter=false,
    dataTableWrapperProps,
    dataName,
    tab,
    // Which preference bucket under `dataName` this table reads/writes. Defaults to
    // `tab`, but lets a list that reuses another list's `tab` (e.g. the invoice list
    // embedded in a customer/group profile, which is a "tab: all" invoice list) keep
    // its own remembered sort and page size instead of sharing the top-level list's.
    preferenceTab,
    savePreference=true,
    // Whether this table remembers its *session* state (search keyword, date/amount
    // range, page position) as well as its display prefs. Turn it off when one
    // preference bucket serves many contexts - e.g. the invoice list on a customer
    // profile, where carrying a search term from one customer to the next is wrong,
    // but remembering the sort column and page size is exactly what we want.
    saveSessionState=true,
    setQueryUrlCallback,
    resetSelectedRowsRef,
    refetchUrlIfTheseParamsChange={},
    hideTableIfNoData,
    onDataChange,
    pageSize,
    syncFiltersToUrl=false, // Enable URL-based filter persistence
    ...rest
}) => {
    const router = useRouter();
    const routerRef = useRef(router);
    routerRef.current = router; // Keep ref updated with latest router
    const dataTablePreferenceCtx = useContext(DataTablePreferenceContext);
    const prefTab = preferenceTab ?? tab;
    // `?? {}` so a dataName with no bucket in DataTablePreferenceProvider's
    // initialState degrades to "nothing remembered" instead of throwing on the
    // destructure below. The bucket is still required for page/sort state to
    // persist at all - this is a guard, not a substitute for registering one.
    const tablePreference = (dataName ? dataTablePreferenceCtx.getPreference({table: dataName, tab: prefTab}) : {}) ?? {};

    // Parse URL params for initial values (if syncFiltersToUrl is enabled)
    const urlFilters = syncFiltersToUrl ? parseUrlFilters(router.query) : {};

    const {
        sort: defaultSort,
        keyword: defaultKeyword,
        dateRange: defaultDateRange,
        amountRange: defaultAmountRange,
        offset: savedOffset,
        limit: savedLimit
    } = tablePreference;
    const {
        defaultFilter: defaultFilterValues
    } = filterParams || {};
    const FilterComponent = filterControls;
    const [displayedData, setDisplayedData] = useState([]);
    // Use URL params first, then context preferences, then undefined
    const [keyword, setKeyword] = useState(urlFilters.keyword ?? defaultKeyword);
    const [dateRange, setDateRange] = useState(urlFilters.dateRange ?? defaultDateRange);
    const [amountRange, setAmountRange] = useState(urlFilters.amountRange ?? defaultAmountRange);
    const [numTotal, setNumTotal] = useState();
    const [limit, setLimit] = useState(savedLimit || pageSize || DEFAULT_ROWS_PER_PAGE);
    // Page position rides in the URL (when syncFiltersToUrl) so it survives Back/reload
    // while a fresh entry (no offset param) starts at page 1. When URL sync is off, fall
    // back to the in-memory preference. For URL-synced lists the URL is authoritative.
    const [offset, setOffset] = useState(
        syncFiltersToUrl ? (urlFilters.offset ?? 0) : (savedOffset || 0)
    );
    const [sort, setSort] = useState(defaultSort);
    const [filterQuery, setFilterQuery] = useState(urlFilters.filterValues ?
        (Array.isArray(urlFilters.filterValues) ? urlFilters.filterValues : [urlFilters.filterValues]) :
        '');
    /*
        Seeded on the first render rather than left empty for an effect to fill in. SWR reads
        an empty key as "do not fetch", so the first render after every mount had no data at
        all, and returning to a list drew a loading skeleton before the cached rows appeared
        a frame later. Everything feeding this is initialised synchronously just above, so
        this is the same URL the effects below would build; they still own every change after
        the first.
    */
    const [queryUrl, setQueryUrl] = useState(
        () => getUrl({keyword, offset, limit, sort, dateRange, amountRange, filterQuery}) || ''
    );

    const [filterValues, setFilterValues] = useState(urlFilters.filterValues ?? defaultFilterValues);
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    // True once we've read filter/page state FROM the URL. We must not push state TO the
    // URL before this, or the transient initial values (e.g. offset=0 while the router is
    // still hydrating router.query) would strip the real params off the URL. Lists that
    // don't sync to the URL are considered "hydrated" immediately.
    const [urlHydrated, setUrlHydrated] = useState(!syncFiltersToUrl);

    // Sync state FROM URL on mount - use router.asPath to detect URL changes
    // This handles both hydration and client-side navigation (back button)
    useEffect(() => {
        if (!syncFiltersToUrl || !router.isReady) return;

        const urlFilters = parseUrlFilters(router.query);

        // Always sync from URL when this effect runs - it only runs on mount
        // or when router.asPath changes (meaning we navigated)
        if (urlFilters.keyword !== undefined) {
            setKeyword(urlFilters.keyword);
        }
        if (urlFilters.dateRange !== undefined) {
            setDateRange(urlFilters.dateRange);
        }
        if (urlFilters.amountRange !== undefined) {
            setAmountRange(urlFilters.amountRange);
        }
        if (urlFilters.filterValues !== undefined) {
            setFilterValues(urlFilters.filterValues);
            setFilterQuery(urlFilters.filterValues);
        }
        // Restore page position from the URL (e.g. Back button / shared link).
        // Absence of the param means page 1.
        setOffset(urlFilters.offset ?? 0);
        // Now safe to start pushing state changes back to the URL.
        setUrlHydrated(true);
    }, [router.isReady, router.asPath, syncFiltersToUrl]);

    // Sync filters to URL when they change
    // Uses routerRef to avoid recreating this callback on router changes,
    // which would cause the sync effect to run on every URL change
    const updateUrlParams = useCallback((updates) => {
        if (!syncFiltersToUrl) return;

        const currentRouter = routerRef.current;
        const currentQuery = { ...currentRouter.query };

        // Remove dynamic route params (e.g. uuid from /customers/[uuid])
        // to avoid duplicating them as query string params
        const routeParams = currentRouter.pathname.match(/\[(\w+)\]/g);
        if (routeParams) {
            routeParams.forEach(param => delete currentQuery[param.slice(1, -1)]);
        }

        // Update or remove params based on values
        // Arrays are passed directly to Next.js which creates repeated params: ?filter=a&filter=b
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === '' ||
                (Array.isArray(value) && value.length === 0)) {
                delete currentQuery[key];
            } else {
                // Keep arrays as-is, Next.js will handle them correctly
                currentQuery[key] = value;
            }
        });

        // Build URL using the resolved path (without query string)
        const basePath = currentRouter.asPath.split('?')[0];
        const queryString = Object.entries(currentQuery)
            .filter(([_, v]) => v !== null && v !== undefined && v !== '')
            .map(([k, v]) => `${k}=${v}`)
            .join('&');
        const newPath = queryString ? `${basePath}?${queryString}` : basePath;

        // Use shallow routing to avoid full page reload
        currentRouter.replace(newPath, undefined, { shallow: true });
    }, [syncFiltersToUrl]); // Note: router removed from deps, using routerRef instead

    // Push state to the URL — but only after we've hydrated FROM the URL, so we never
    // overwrite real URL params with transient initial values (StrictMode-safe).
    useEffect(() => {
        if (syncFiltersToUrl && urlHydrated) {
            const updates = {};
            if (keyword) updates.q = keyword;
            else updates.q = null;

            // Use comma-separated format: ?filter=a,b
            if (filterValues && (Array.isArray(filterValues) ? filterValues.length > 0 : filterValues)) {
                updates.filter = Array.isArray(filterValues) ? filterValues.join(',') : filterValues;
            } else {
                updates.filter = null;
            }

            // Handle date range - dateRange is [start, end] array
            if (Array.isArray(dateRange) && dateRange[0] !== undefined && dateRange[0] !== null) {
                updates.dateFrom = dateRange[0];
            } else {
                updates.dateFrom = null;
            }
            if (Array.isArray(dateRange) && dateRange[1] !== undefined && dateRange[1] !== null) {
                updates.dateTo = dateRange[1];
            } else {
                updates.dateTo = null;
            }

            // Handle amount range - amountRange is [min, max] array
            if (Array.isArray(amountRange) && amountRange[0] !== undefined && amountRange[0] !== null) {
                updates.amountMin = amountRange[0];
            } else {
                updates.amountMin = null;
            }
            if (Array.isArray(amountRange) && amountRange[1] !== undefined && amountRange[1] !== null) {
                updates.amountMax = amountRange[1];
            } else {
                updates.amountMax = null;
            }

            // Page position - keep it in the URL so Back/reload restore it; page 1 omits it
            updates.offset = offset > 0 ? offset : null;

            updateUrlParams(updates);
        }
    }, [keyword, filterValues, dateRange, amountRange, offset, syncFiltersToUrl, urlHydrated, updateUrlParams]);

    useEffect(() => {
        setQueryUrlCallback && setQueryUrlCallback(queryUrl);
    }, [queryUrl]);

    // Create a stable string key from refetchUrlIfTheseParamsChange for dependency comparison
    const refetchKey = useMemo(() => JSON.stringify(refetchUrlIfTheseParamsChange), [refetchUrlIfTheseParamsChange]);

    // Snapshot of the params that should send the user back to page 1 when they change.
    // We compare values (not "is this the first render") so that a remembered offset/page
    // restored from preferences survives mount — including React StrictMode's double-invoke
    // of effects in development, which fires this effect twice with identical values.
    const prevParamsKey = useRef(null);
    useEffect(() => {
        /*
         * When the data table's param (i.e. search keyword, sort column, date range, or filter) changes, we want to
         * reset offset to 0. The problem here is:
         * 1. When you update any of the param, useEffect pick up the value change and initiate a GET call with new param
         * 2. Since param is updated, data table resets offset to 0. And since offset changes, useEffect once
         *     again picks up the value change and initiate another GET call with new offset
         *
         * Resulting in duplicate GET call.
         *
         * To make only one call, the logic now is to rely on the GET call in (2) and skip (1).
         * However, when offset is already 0, we still want (1) to happen. Otherwise (2) won't trigger since offset=0 is not
         * a real value. This is captured in the "if (offset == 0)" case.
         */
        const paramsKey = JSON.stringify({keyword, sort, dateRange, amountRange, filterQuery, refetchKey});
        const isRealParamChange = prevParamsKey.current !== null && prevParamsKey.current !== paramsKey;
        prevParamsKey.current = paramsKey;

        if (isRealParamChange && offset !== 0) {
            setOffset(0);
            savePreference && saveSessionState && dataName && dataTablePreferenceCtx.updatePreferences({
                table: dataName,
                key: 'offset',
                tab: prefTab,
                value: 0,
            });
        }
        setResetPaginationToggle(prev => !prev);
        if (offset === 0) {
            const url = getUrl({keyword, offset, limit, sort, dateRange, amountRange, filterQuery});
            url && setQueryUrl(url);
        }
    }, [keyword, sort, dateRange, amountRange, filterQuery, refetchKey]);

    useEffect(() => { // update the query url used to fetch the data
        const url = getUrl({keyword, offset, limit, sort, dateRange, amountRange, filterQuery});
        url && setQueryUrl(url);
    }, [offset, limit, refetchKey]);


    /*
        Back used to redraw the list from scratch. That was never really about the refetch -
        it was that the table dropped to its loading skeleton while the request was in
        flight, even though SWR had the rows cached. Clearing isLoading whenever data is
        present (below) keeps the cached rows on screen and lets the revalidation happen
        behind them.

        Deliberately not deduped beyond SWR's default: a longer window also swallows the
        refetches callers ask for. Remounting this table via a React key, which is how
        TransactionList refreshes after a customer change, rebuilds the identical queryUrl,
        so a wide dedupe window would serve the stale row straight back.
    */
    const {data} = useSWR(
        queryUrl,
        silentFetcher,
        {
            revalidateOnFocus: false,
            keepPreviousData: true,
            onSuccess: () => setIsLoading(false)
        }
    );

    useEffect(() => {
        if (!data) return;
        /*
            Cleared here rather than only in SWR's onSuccess. onSuccess fires on a completed
            request, so when the cache answers instead - which is the whole point of the
            deduping above - it never ran, and the table sat on its loading skeleton with the
            real rows already in hand.
        */
        setIsLoading(false);
        const reshapedData = reshape(data);
        // Extract total after reshape, since reshape may set data.total
        const { total } = data;
        // Compare against undefined rather than truthiness: a list that empties out reports
        // total 0, and skipping it leaves the previous count in place, so the footer and
        // pagination keep claiming a row that is no longer rendered.
        if (total !== undefined) setNumTotal(total);
        reshapedData && setDisplayedData(reshapedData);
        onDataChange && onDataChange(reshapedData, data); // callback with raw data
    }, [data]);

    const handleFilterChange = (value) => {
        setFilterQuery(value);
        setFilterValues(value);
    }

    return (
        <Box>
            {(isLoading || (displayedData && !(hideTableIfNoData && displayedData.length === 0))) && (
                <DataTable
                    resetSelectedRowsRef={resetSelectedRowsRef}
                    data={displayedData}
                    columns={columns}
                    exportConfigs={getCSVConfig([...columns, { selector: "invoice_link" }])}
                    {...(onRowClicked ? {onRowClicked} : {})}
                    numRows={numTotal}
                    {...(hideSearch ? {} : { onSearch: str => {
                        setKeyword(str);
                        if (dataName && savePreference && saveSessionState) {
                            dataTablePreferenceCtx.updatePreferences({
                                table: dataName,
                                key: 'keyword',
                                tab: prefTab,
                                value: str
                            });
                        }
                    }})}
                    {...(hideDateRangeFilter ? {} : { onDateRangeSet: dateRange => {
                        setDateRange(dateRange);
                        if (dataName && savePreference && saveSessionState) {
                            dataTablePreferenceCtx.updatePreferences({
                                table: dataName,
                                key: 'dateRange',
                                tab: prefTab,
                                value: dateRange
                            });
                        }
                    }})}
                    {...(hideAmountRangeFilter ? {} : { onAmountRangeSet: amountRange => {
                        setAmountRange(amountRange);
                        if (dataName && savePreference && saveSessionState) {
                            dataTablePreferenceCtx.updatePreferences({
                                table: dataName,
                                key: 'amountRange',
                                tab: prefTab,
                                value: amountRange
                            });
                        }
                    }})}
                    {...(filterControls) ? {controls: <FilterComponent filterValues={filterValues} onApplyFilter={handleFilterChange} tab={tab}/>} : {}}
                    dateRange={dateRange}
                    amountRange={amountRange}
                    defaultSort={defaultSort}
                    resetPaginationToggle={resetPaginationToggle}
                    limit={limit}
                    paginationDefaultPage={limit ? Math.floor(offset / limit) + 1 : 1}
                    onOffsetChange={(newOffset, newLimit) => {
                        if (dataName) {
                            if (newOffset !== offset) {
                                setOffset(newOffset);
                                savePreference && saveSessionState && dataTablePreferenceCtx.updatePreferences({
                                    table: dataName,
                                    key: 'offset',
                                    tab: prefTab,
                                    value: newOffset,
                                });
                            }
                            if (newLimit !== limit) {
                                setLimit(newLimit);
                                savePreference && dataTablePreferenceCtx.updatePreferences({
                                    table: dataName,
                                    key: 'limit',
                                    tab: prefTab,
                                    value: newLimit,
                                });
                            }
                        }
                    }}
                    onSort={(col, direction) => {
                        if ( isUndefined(col) ) return; // Guard against empty sort col issue.

                        setSort({col, direction});
                        if (dataName && savePreference)
                            dataTablePreferenceCtx.updatePreferences({
                                table: dataName,
                                key: 'sort',
                                tab: prefTab,
                                value: {direction, col}
                            });
                    }}
                    getFullData={async () => {
                        const url = getUrl({
                            keyword,
                            offset,
                            limit: NUM_ROWS_EXPORTED_IN_CSV,
                            dateRange: dateRange || null,
                            filterQuery: filterQuery || '',
                        });
                        const response = await fetch(url);
                        const data = await response.json();
                        return data ? reshape(data) : null;
                    }}
                    dataTableWrapperProps={dataTableWrapperProps}
                    dataName={dataName}
                    savePreference={savePreference}
                    tab={tab}
                    defaultSearchText={defaultKeyword}
                    isLoading={isLoading}
                    {...(pageSize ? {defaultRowPerPage: pageSize} : {})}
                    {...rest}
                />
            )}
        </Box>
    );
};

export default React.memo(DataTableWithBackendSearch);
